import { useMemo } from "react";
import { useQuery } from "react-query";

import { useAuth } from "../../auth/useAuth";
import { ORGANISATION_FEATURE_FLAGS } from "../../constants/featureFlags";
import { UserSettingName } from "../../types/UserSetting";
import { api } from "../api";
import { APIError } from "../api-types";
import { useGetOrganisationFeatureFlag } from "../organisations/hooks";
import { MAX_PAGE_LIMIT, pageQueryParams } from "../pagination";
import { useGetUserSetting } from "../settings/hooks";
import type { PortfolioLiveFallbackReason, PortfolioWindowParams, PropertyPortfolioPage } from "./types";
import { type PortfolioLiveRange, toPortfolioWindowParams } from "./window";

export const PORTFOLIO_ENERGY_QUERY_KEY = "portfolio-energy";
export const PORTFOLIO_PATH = "experience/v1/property/portfolio";

/** Bounded like fetchAllPages: 200 pages × 200 rows is far past the largest organisation. */
const MAX_PAGES = 200;

/**
 * Why the page uses the live path: a field user's session, a 404 (the flag is off), totals
 * with no `dailyEnergy` (a defensive guard: the 5.2c-B contract always sends it), or the
 * API's own `liveFallback.reason` (5.2c-B), passed through as sent. A reason the API adds
 * later is not in this union yet, and still falls back (spec §6.2).
 */
export type PortfolioFallbackReason =
  | "assigned-properties-only"
  | "not-enabled"
  | "daily-energy-missing"
  | PortfolioLiveFallbackReason;

/**
 * Every page of the window, or why the page must use the live path instead (spec §6.2).
 * Only these cases fall back; anything else is an error the page shows.
 */
export type PortfolioFetchResult =
  | { kind: "pages"; pages: PropertyPortfolioPage[] }
  | { kind: "fallback"; reason: PortfolioFallbackReason };

/**
 * Every property's energy over the window, fetched in pages.
 *
 * Every property, because the map draws a marker for each (spec §6.2). A local loop rather
 * than fetchAllPages: that helper returns items only, and the first page's `orgTotals` and
 * `orgTotalsOmittedBecause` are what this page needs most. Same stop rules — `total`
 * reached, or a page that adds nothing (so a total that shrinks mid-walk cannot loop).
 *
 * A 404 anywhere in the walk means ORG_ROLLUP_READS was turned off after the flags were
 * read, and a `liveFallback` anywhere means the rollup cannot match live for this request;
 * either way the pages already held are discarded rather than shown as a partial portfolio.
 *
 * `signal` is react-query's: it aborts once the walk's observer has moved on (a new window,
 * a new organisation, leaving the page). The walk then asks for no further page, and an
 * abort is never read as a fallback — whatever the page in flight answers, a 404 included.
 */
export async function fetchPortfolioEnergy(
  params: PortfolioWindowParams,
  signal?: AbortSignal,
): Promise<PortfolioFetchResult> {
  // Exactly what organisation-power is sent for the same page (lib/api/readings/hooks.ts):
  // its two instants, and `useV2` only when it is true. Never `useV2=false`: the query-string
  // builder sends a literal false, and 5.2c-B answers any value but `true` with a 400.
  const windowQuery: Record<string, string> = {
    fromTimestamp: params.fromTimestamp,
    toTimestamp: params.toTimestamp,
  };
  if (params.useV2) {
    windowQuery.useV2 = "true";
  }
  const pages: PropertyPortfolioPage[] = [];
  let held = 0;

  try {
    for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex++) {
      if (signal?.aborted) {
        throw new Error("Portfolio walk aborted");
      }

      const page = await api.get<PropertyPortfolioPage>(PORTFOLIO_PATH, {
        ...windowQuery,
        ...pageQueryParams({
          limit: MAX_PAGE_LIMIT,
          offset: pageIndex * MAX_PAGE_LIMIT,
          sort: "kwh",
          desc: true,
        }),
      });

      if (signal?.aborted) {
        throw new Error("Portfolio walk aborted");
      }

      // A field user's session gets no organisation totals; the live path is what that
      // user sees today, so the whole energy section stays on it.
      if (pageIndex === 0 && page.orgTotalsOmittedBecause === "assigned-properties-only") {
        return { kind: "fallback", reason: "assigned-properties-only" };
      }

      // The rollup cannot match live exactly for this request (a V1 basis, a window that
      // starts before the organisation's verified coverage, a rebuild still owed, days the
      // rollup has not written, energy it could not place), so the page shows live, with no
      // banner (spec §3.2–§3.4, §6.2). Every reason falls back, one this client does not
      // know yet included. The API sets it on every page.
      if (page.liveFallback) {
        return { kind: "fallback", reason: page.liveFallback.reason };
      }

      if (pageIndex === 0) {
        // `filtered` / `no-date-range` cannot happen — no search is sent and a window always
        // is — so if one does, the request is wrong and must be seen, not papered over.
        if (!page.orgTotals) {
          throw new Error(
            `Portfolio returned no organisation totals (${page.orgTotalsOmittedBecause ?? "no reason given"})`,
          );
        }

        // The Daily average tile draws this series. This guard is defensive: the 5.2c-B
        // contract always sends it with totals. Were it ever missing, the tile would draw
        // seven 0 kWh bars beside real totals, so the page shows live's exact figures
        // instead, silently. It does not cover a deploy or rollback of 5.2c-B: an API task
        // without it ignores the instants and answers this request with a 400 first (the
        // banner), and only un-flagging the organisation restores live then.
        if (!Array.isArray(page.orgTotals.dailyEnergy)) {
          return { kind: "fallback", reason: "daily-energy-missing" };
        }
      }

      pages.push(page);
      held += page.items.length;

      if (page.items.length === 0 || held >= page.total) {
        break;
      }
    }
  } catch (error) {
    if (!signal?.aborted && error instanceof APIError && error.statusCode === 404) {
      return { kind: "fallback", reason: "not-enabled" };
    }
    throw error;
  }

  return { kind: "pages", pages };
}

/**
 * At most one retry, and only for a server error (the API answers 503 when it throttles).
 *
 * Every retry re-walks from page 0, so react-query's default of three would multiply a
 * failure on a late page and hold the error banner back behind seconds of backoff. A 4xx,
 * or the deliberate "no organisation totals" error, would get the same answer again.
 */
export function shouldRetryPortfolioWalk(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && error instanceof APIError && error.statusCode >= 500;
}

/**
 * The aggregates basis organisation-power reads for this session: V2 when the organisation's
 * USE_V2_AGGREGATES flag or the user's own setting is on. This is useAggregatesV2Query's rule
 * (lib/hooks/useAggregatesV2Query.ts), restated because that hook owns its query and the
 * walk needs the value in its key. `isLoading` holds until both have loaded, as that hook's
 * `enabled` does: a walk sent before then could ask on the wrong basis.
 */
export function usePortfolioAggregatesBasis(): { useV2: boolean; isLoading: boolean } {
  const { value: orgUseV2Aggregates, isLoading: orgFlagsLoading } = useGetOrganisationFeatureFlag(
    ORGANISATION_FEATURE_FLAGS.USE_V2_AGGREGATES,
  );
  const { value: userUseV2Aggregates, isLoading: userSettingLoading } = useGetUserSetting<boolean>(
    UserSettingName.UseV2Aggregates,
    "boolean",
  );

  return {
    useV2: Boolean(orgUseV2Aggregates || userUseV2Aggregates),
    isLoading: orgFlagsLoading || userSettingLoading,
  };
}

/**
 * The portfolio endpoint's energy for the session's organisation (org-scale PR 5.2), over
 * organisation-power's own range and basis (5.2c).
 *
 * Keyed on the window's instants and basis: the page re-anchors a preset's instants to the
 * next top of the hour each time it is clicked, so a re-click once the hour has turned walks
 * again, exactly as organisation-power refetches under its own instant-keyed query.
 *
 * Keyed on the organisation as well: an in-SPA organisation switch does not clear the
 * react-query cache, and refetchOnMount is off app-wide. The request itself is scoped by the
 * token, though, so the key alone cannot stop a walk that outlives its key from caching
 * another organisation's figures under it. Reading `signal` is what prevents that:
 * react-query 3 cancels and REVERTS a query whose last observer leaves only when its query
 * function consumed the signal (query.js removeObserver); otherwise it lets the fetch finish
 * into the cache. AuthProvider also removes these entries on an organisation change.
 *
 * Keyed on the user and their role too: what the walk returns depends on who asks (a field
 * user gets no organisation totals), and a sign-out followed by another user's sign-in on the
 * same tab keeps the organisation and the cache. The key always starts with
 * PORTFOLIO_ENERGY_QUERY_KEY, which AuthProvider's removals match as a prefix.
 */
export function usePortfolioEnergy(range: PortfolioLiveRange, enabled: boolean) {
  const { user } = useAuth();
  const organisationId = user?.currentOrganisationId;
  const userId = user?.id;
  const basis = usePortfolioAggregatesBasis();

  const { fromTimestamp, toTimestamp } = range;
  const params = useMemo(
    () => toPortfolioWindowParams({ fromTimestamp, toTimestamp }, basis.useV2),
    [fromTimestamp, toTimestamp, basis.useV2],
  );

  return useQuery<PortfolioFetchResult, Error>(
    [PORTFOLIO_ENERGY_QUERY_KEY, organisationId, userId, user?.currentOrganisationRoleId, params],
    ({ signal }) => fetchPortfolioEnergy(params, signal),
    {
      enabled: enabled && !basis.isLoading && Boolean(organisationId) && Boolean(userId),
      retry: shouldRetryPortfolioWalk,
      // As organisation-power's; the server caches its fold for 15 ± 2 minutes anyway.
      staleTime: 600000,
    },
  );
}
